/**
 * VerificationBadge.js — Institutional Data Integrity Indicator
 * Shows data provenance: Self-Declared / Programme Reviewed / Independently Verified
 * Critical for DFI credibility — they will challenge data defensibility.
 */
import React from "react";
import { CheckCircle2, AlertTriangle, Shield, Eye } from "lucide-react";

const TIERS = {
  "self_declared": {
    label: "Self-Declared",
    sublabel: "Not independently reviewed",
    color: "#d97706",
    bg: "#fffbeb",
    border: "#fde68a",
    Icon: AlertTriangle,
    confidence: "Low",
    description: "Data submitted by applicant. Subject to programme review.",
  },
  "programme_reviewed": {
    label: "Programme Reviewed",
    sublabel: "LCFPI assessment complete",
    color: "#1e40af",
    bg: "#eff6ff",
    border: "#bfdbfe",
    Icon: Eye,
    confidence: "Medium",
    description: "Reviewed and assessed by LCFPI programme management. Data confidence validated.",
  },
  "third_party_verified": {
    label: "Third-Party Verified",
    sublabel: "Independently validated",
    color: "#059669",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    Icon: CheckCircle2,
    confidence: "High",
    description: "Independently verified by accredited third-party assessor. Highest trust level.",
  },
  "vvb_ready": {
    label: "VVB Pre-Qualified",
    sublabel: "Ready for validation",
    color: "#6366f1",
    bg: "#eef2ff",
    border: "#c7d2fe",
    Icon: Shield,
    confidence: "High",
    description: "Data quality meets VVB pre-qualification threshold (≥65/100 confidence).",
  },
};

/**
 * Compute verification status from project data.
 */
export function computeVerificationStatus(project) {
  const conf = project.data_confidence || project.climate_score || 45;
  const status = project.status || "draft";
  const evidenceLevel = project.evidence_level || "estimated";
  const reviewedBy = project.reviewed_by;

  if (reviewedBy && conf >= 65) return "vvb_ready";
  if (reviewedBy) return "programme_reviewed";
  if (["tier_assigned","investor_ready","due_diligence","financing_secured"].includes(status)) return "programme_reviewed";
  return "self_declared";
}

/**
 * Inline badge (for tables and cards).
 */
export function VerificationBadge({ status, compact = false, showIcon = true, testId = "" }) {
  const tier = TIERS[status] || TIERS.self_declared;
  const { Icon } = tier;
  if (compact) {
    return (
      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
        background: tier.bg, border: `1px solid ${tier.border}`, color: tier.color,
        display: "inline-flex", alignItems: "center", gap: 4 }}
        data-testid={testId || `verification-badge-${status}`}>
        {showIcon && <Icon size={8} />}
        {tier.label}
      </span>
    );
  }
  return (
    <div style={{ background: tier.bg, border: `1px solid ${tier.border}`, borderRadius: 8,
      padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}
      data-testid={testId || `verification-badge-${status}`}>
      <Icon size={13} style={{ color: tier.color, flexShrink: 0 }} />
      <div>
        <p style={{ fontSize: 10, fontWeight: 700, color: tier.color }}>{tier.label}</p>
        <p style={{ fontSize: 8, color: "#94a3b8" }}>{tier.sublabel}</p>
      </div>
      <span style={{ marginLeft: "auto", fontSize: 8, fontWeight: 700, color: tier.color,
        background: tier.color + "15", padding: "1px 6px", borderRadius: 3 }}>
        {tier.confidence} Confidence
      </span>
    </div>
  );
}

/**
 * Evidence completeness score gauge.
 */
export function EvidenceCompletenessScore({ score = 0, label = "Evidence Completeness", compact = false }) {
  const color = score >= 75 ? "#059669" : score >= 50 ? "#d97706" : "#ef4444";
  const band = score >= 75 ? "Strong" : score >= 50 ? "Partial" : "Weak";
  if (compact) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ width: 40, height: 4, background: "#f1f5f9", borderRadius: 2, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${score}%`, background: color, borderRadius: 2 }} />
        </div>
        <span style={{ fontSize: 9, fontWeight: 700, color }}>{score}%</span>
      </div>
    );
  }
  return (
    <div data-testid="evidence-completeness-score">
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 10, color: "#475569", fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 900, color }}>{score}% — {band}</span>
      </div>
      <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${score}%`, background: color, borderRadius: 3, transition: "width .5s ease" }} />
      </div>
      <p style={{ fontSize: 8, color: "#94a3b8", marginTop: 3 }}>
        {score < 50 ? "Insufficient for DFI submission — complete financial & governance docs" :
         score < 75 ? "Approaching DFI threshold — minor gaps remain" :
         "Meets DFI documentation standard"}
      </p>
    </div>
  );
}

/**
 * Provenance indicator for assessment outputs.
 * Shows what evidence level underlies the calculation.
 */
export function ProvenanceIndicator({ evidenceLevel, dataConfidence, assessmentVersion = 1 }) {
  const colors = { measured: "#059669", declared: "#1e40af", estimated: "#d97706" };
  const color = colors[evidenceLevel] || "#94a3b8";
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6,
      background: "#f8fafc", border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "4px 8px" }}
      data-testid="provenance-indicator">
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
      <span style={{ fontSize: 8, color: "#475569" }}>
        {evidenceLevel?.charAt(0).toUpperCase() + evidenceLevel?.slice(1) || "Estimated"} evidence
        {" \u00b7 "}
        {dataConfidence || 45}/100 confidence
        {" \u00b7 "}
        Assessment v{assessmentVersion}
      </span>
    </div>
  );
}

export default VerificationBadge;
